'use client';

import { AnimatePresence, m } from 'motion/react';
import * as React from 'react';

import { cn } from '@heidi/utils';
import { useDesignSystemIntl } from '../../../hooks/use-design-system-intl';
import { ScrollArea } from '../../../scroll-area';

import { usePanelContext } from './panel-context';
import {
  panelHeaderClasses,
  panelPinnedClasses,
  panelPinnedContainerClasses,
  panelPinnedWrapperClasses,
} from './panel.styles';
import { PANEL_PINNED_ID, PANEL_WIDTH, type PanelPinnedProps } from './panel.types';

/**
 * Pinned panel component that displays as a secondary sidebar.
 * Renders between the primary sidebar and main content.
 * Styled to match the sidebar look and feel.
 *
 * @example
 * ```tsx
 * <PageShell.Sidebar>
 *   ...
 * </PageShell.Sidebar>
 * // PanelPinned renders automatically after sidebar when pinned
 * ```
 */
const PanelPinned = React.forwardRef<HTMLDivElement, PanelPinnedProps>(
  ({ 'data-testid': testId, className, ...props }, ref) => {
    const { isPinned, registeredPanels, pageId, isMobile, setPanelRef } =
      usePanelContext();
    const intl = useDesignSystemIntl();
    const panelLabel = intl.formatMessage({
      defaultMessage: 'Panel',
      id: '7N7VzfQghX',
      description: 'Accessible label for the pinned secondary panel region',
    });

    // Callback ref to register the panel element for focus management
    const asideRef = React.useCallback(
      (element: HTMLElement | null) => {
        setPanelRef(element);
      },
      [setPanelRef]
    );

    // Don't render on mobile (mobile uses drawer-based panel)
    if (isMobile) {
      return null;
    }

    // Get panel registration for the current page
    // This ensures pinned panel always shows the current page's content and action
    const pinnedPanel = pageId ? (registeredPanels.get(pageId) ?? null) : null;
    const pinnedContent = pinnedPanel?.content ?? null;
    const pinnedAction = pinnedPanel?.action;

    // Always render wrapper for smooth width transitions, but with 0 width when not pinned
    const width = isPinned && pinnedContent ? PANEL_WIDTH : '0px';

    return (
      <div
        ref={ref}
        data-testid={testId}
        data-slot="panel-pinned"
        data-state={isPinned ? 'open' : 'closed'}
        className={cn(panelPinnedWrapperClasses, className)}
        style={{ width }}
        {...props}
      >
        {isPinned && pinnedContent && (
          <aside
            ref={asideRef}
            id={PANEL_PINNED_ID}
            className={panelPinnedClasses}
            aria-label={panelLabel}
            // tabIndex={-1} makes element focusable programmatically but not via Tab
            // This allows focus to move here when panel opens, without adding to tab order
            //
            // TODO(@jordan): Consider keyboard navigation enhancements. Note that "Escape to
            // return focus to trigger" is NOT recommended here - that pattern is for temporary
            // modal/popup content (dialogs, menus). This is a persistent, non-modal panel that
            // coexists with main content. Users should navigate freely between panel and main
            // content using normal Tab flow. Forcing focus back on Escape would be disorienting
            // and violate expected behavior for sidebar-like regions (ARIA landmark: complementary).
            tabIndex={-1}
          >
            <div className="flex flex-col">
              {/* Header with optional action button (matches floating panel header without maximize button) */}
              {pinnedAction && (
                <div className={panelHeaderClasses}>
                  <div className="shrink-0">{pinnedAction}</div>
                </div>
              )}

              {/*
                ScrollArea viewportClassName explained:
                - [&>div]:!block [&>div]:!w-full: CRITICAL - Radix ScrollArea has an internal
                  div with display:table that ignores parent width constraints, causing content
                  to overflow. These selectors force it to display:block and width:100% so
                  content properly respects the panel's fixed width and truncates correctly.
              */}
              <ScrollArea
                className={panelPinnedContainerClasses}
                viewportClassName="[&>div]:!block [&>div]:!w-full"
              >
                {/* AnimatePresence for smooth content swapping when navigating between pages */}
                <AnimatePresence mode="wait" initial={false}>
                  <m.div
                    key={pageId ?? 'empty'}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{
                      duration: 0.15,
                      ease: [0.25, 0.46, 0.45, 0.94], // ease-out-quad
                    }}
                  >
                    {pinnedContent}
                  </m.div>
                </AnimatePresence>
              </ScrollArea>
            </div>
          </aside>
        )}
      </div>
    );
  }
);
PanelPinned.displayName = 'PanelPinned';

export { PanelPinned };
